import { z } from 'zod';

import {
  DELIVERY_COMPANIES,
  KOSHER_CERTIFICATIONS,
  MENU_SOURCES,
} from 'features/onboarding/types/onboarding.types';

export const businessQuestionsSchema = z
  .object({
    deliveryCompanies: z.array(z.enum(DELIVERY_COMPANIES)).default([]),
    otherDeliveryCompanies: z.string().optional().or(z.literal('')),
    // Derived from `kosherCertifications` by the business step, never asked.
    isKosher: z.boolean().nullish(),
    kosherCertifications: z.array(z.enum(KOSHER_CERTIFICATIONS)).default([]),
    menuSource: z.enum(MENU_SOURCES, {
      errorMap: () => ({ message: 'required' }),
    }),
    menuWebsiteUrl: z.string().optional().or(z.literal('')),
    menuFile: z
      .object({
        name: z.string(),
        type: z.string(),
        size: z.number(),
        url: z.string(),
        uploadedAt: z.string(),
      })
      .optional(),
    posType: z.string().optional().or(z.literal('')),
    wantsMenuPhotoShoot: z.boolean({ required_error: 'required' }),
  })
  // "Other" selected → free-text must be filled in.
  .refine(
    (data) =>
      !data.deliveryCompanies.includes('other') ||
      (data.otherDeliveryCompanies?.trim().length ?? 0) > 0,
    { path: ['otherDeliveryCompanies'], message: 'required' },
  )
  // Menu via website → a URL is required.
  .refine(
    (data) =>
      data.menuSource !== 'website' || (data.menuWebsiteUrl?.trim().length ?? 0) > 0,
    { path: ['menuWebsiteUrl'], message: 'required' },
  )
  // Menu via upload → a file is required.
  .refine((data) => data.menuSource !== 'upload' || !!data.menuFile, {
    path: ['menuFile'],
    message: 'documentRequired',
  })
  // Menu via POS → a POS system (or free text) is required.
  .refine(
    (data) => data.menuSource !== 'pos' || (data.posType?.trim().length ?? 0) > 0,
    { path: ['posType'], message: 'required' },
  );

export type BusinessQuestionsInput = z.infer<typeof businessQuestionsSchema>;
